import type { ReactNode, TextareaHTMLAttributes } from 'react'
import { forwardRef } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import cn from 'classnames'
import { Field } from 'formik'

import useBranding from '../hooks/useBranding'

type TFormikField = {
  name?: string
  value?: unknown
  onBlur?: (...args: unknown[]) => void
  onChange?: (...args: unknown[]) => void
}

type TFormikProps = {
  field: TFormikField
}

type Props = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'style' | 'value'
> & {
  className?: string
  field?: TFormikField
  label?: ReactNode
  labelHidden?: boolean
  minRows?: number
  name?: string
  noFormik?: boolean
  renderLabelAction?: () => ReactNode
  required?: boolean
  value?: string
}

const Textarea = forwardRef<HTMLTextAreaElement, Props>((props, ref) => {
  const {
    id,
    label,
    labelHidden,
    required,
    className,
    name,
    field = {},
    value,
    minRows,
    renderLabelAction,
    ...rest
  } = props

  const [{ primaryColorPalette }] = useBranding()

  const textareaCn = cn(
    [
      'shadow-xs',
      'block w-full',
      'rounded-md',
      'border-gray-300',
      'sm:text-sm',
      `focus:border-${primaryColorPalette}-500 focus:ring-${primaryColorPalette}-500`,
    ],
    { 'mt-1': !labelHidden },
    className,
  )

  const renderLabel = () => {
    const labelCn = cn({
      'block text-sm font-medium leading-5 text-black': !labelHidden,
      required: required,
      'sr-only': labelHidden,
    })

    if (renderLabelAction) {
      return (
        <div className="flex items-center justify-between">
          <label htmlFor={id} className={labelCn}>
            {label}
          </label>
          {renderLabelAction()}
        </div>
      )
    }

    return (
      <label htmlFor={id} className={labelCn}>
        {label}
      </label>
    )
  }

  return (
    <div>
      {renderLabel()}
      <TextareaAutosize
        className={textareaCn}
        id={id}
        minRows={minRows || 3}
        name={name}
        ref={ref}
        value={(field.value as string | undefined) ?? value ?? ''}
        {...rest}
      />
    </div>
  )
})

const TextareaControl = forwardRef<HTMLTextAreaElement, Props>((props, ref) => {
  const { noFormik = true, name, ...rest } = props

  if (noFormik) {
    return <Textarea ref={ref} name={name} {...rest} />
  }

  return (
    <Field name={name}>
      {(formikProps: TFormikProps) => (
        <Textarea ref={ref} name={name} {...rest} {...formikProps} />
      )}
    </Field>
  )
})

export default TextareaControl
